import jwtDecode from "jwt-decode";
import { toast } from "react-toastify";
import { getLocalStorage, clearLocalStorageOnLogout } from "./common";
import { USER_INFO_KEY } from "../constants/common";

// Decodes an access token's JWT payload, returning null instead of
// throwing for a missing/malformed token so callers don't each need
// their own try/catch.
const decodeToken = (accessToken) => {
  if (!accessToken) return null;
  try {
    return jwtDecode(accessToken) || null;
  } catch {
    return null;
  }
};

// Single-slot memo keyed on the token string itself, shared by every
// decode below: within one tab there's only ever one active access token
// at a time (aside from a brief login/logout transition), and
// crossLoginHelper.js's isSSOLogin() calls isImpersonatedToken() on
// ordinary (non-impersonated) render paths - e.g. twice per render in the
// profile pages - so re-decoding the same JWT on every call, or decoding
// it twice per call (once for the impersonatedBy check, again for exp),
// is pure waste for the overwhelming majority of sessions that are never
// impersonated at all.
let lastToken;
let lastPayload = null;
const decodeTokenMemoized = (accessToken) => {
  if (accessToken !== lastToken) {
    lastToken = accessToken;
    lastPayload = decodeToken(accessToken);
  }
  return lastPayload;
};

// Source of truth for "is this session impersonated" is the
// `impersonatedBy` claim the API puts directly in the access token when
// it mints one from an admin-dashboard impersonation grant (see
// Gitcha-API#659) - not a self-assigned providerLogin string. Reading it
// off the token itself means this can't be faked by editing localStorage,
// and can't be silently dropped by some other call site rewriting
// USER_INFO_KEY with a different provider (e.g. VerifyMLSAccountModal's
// "saml").
export const isImpersonatedToken = (accessToken) =>
  Boolean(decodeTokenMemoized(accessToken)?.impersonatedBy);

// Impersonated sessions get no refresh token (see Gitcha-API#659), so
// they never pass through getHeaders()'s expiry check, which only runs
// when a refresh token is present - without this, an expired
// impersonated session would 401 on every call while the banner kept
// claiming the admin was still signed in as the target user.
const getTokenExpiryMs = (accessToken) => {
  const exp = decodeTokenMemoized(accessToken)?.exp;
  return typeof exp === "number" ? exp * 1000 : null;
};

// setTimeout's delay is a 32-bit signed int internally - anything above
// this fires near-immediately instead of after the intended delay
// (browsers silently clamp/overflow rather than throwing). Not reachable
// with the real 15-minute impersonation TTL, but a malformed/absurd `exp`
// claim should end the session late, not instantly - used by
// ImpersonationBanner's own expiry timer.
export const MAX_SET_TIMEOUT_MS = 2 ** 31 - 1;
export const clampToMaxTimeout = (ms) => Math.min(ms, MAX_SET_TIMEOUT_MS);

// Returns null for a normal session, or a small summary for the banner
// when the current session was started via admin-dashboard impersonation.
export const getImpersonationSession = () => {
  const userInfo = getLocalStorage(USER_INFO_KEY);
  const accessToken = userInfo?.token?.accessToken;
  if (!isImpersonatedToken(accessToken)) {
    return null;
  }

  const name = userInfo?.userInfo?.name?.trim();
  return {
    userName: name || userInfo?.userInfo?.email || "this user",
    expiresAt: getTokenExpiryMs(accessToken),
  };
};

// Ends an impersonated session. Idempotent - if USER_INFO_KEY is already
// gone, a previous call (or a manual click racing the expiry timer)
// already handled it, so this is a no-op rather than a second
// window.close()/redirect. Returns whether it actually acted, so a caller
// holding session state in memory (ImpersonationBanner) knows whether it
// needs to clear that state itself - clearing localStorage alone doesn't
// trigger a re-render, and same-tab writes don't fire a "storage" event.
//
// { silent: true } is for the expiry path (ImpersonationBanner's own
// timer, not an admin click): the session died on its own, so - unlike
// the manual "End Session" button - closing the tab or navigating away
// would erase the only explanation the admin gets for why the banner
// just vanished. Leaves the tab where it is and shows a toast instead.
//
// This tab was opened from the admin dashboard (see
// Gitcha-Admin-Frontend's Sign In As action), so window.close() usually
// works for a manual end; falls back to a plain redirect for a browser
// that blocks script-initiated tab closes. window.close() is
// asynchronous, so the fallback runs on a short delay rather than
// immediately - otherwise a successful close still flashes "/" first.
export const endImpersonation = ({ silent = false } = {}) => {
  if (!getLocalStorage(USER_INFO_KEY)) return false;

  clearLocalStorageOnLogout();

  if (silent) {
    toast.warning("Your impersonated session has expired.");
    return true;
  }

  window.close();
  setTimeout(() => {
    window.location.href = "/";
  }, 100);
  return true;
};
